// src/context/SelectedOptionsContext.jsx
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
  useCallback,
} from "react";
import { createUrlConfigString } from "../helpers/config-utilities";

const STORAGE_KEY = "selectedOptions_v1";
const STORAGE_KEY_CONFIG = "selectedConfig_v1";

const SelectedOptionsContext = createContext(null);

function getGroupKeyFromDescriptor(descriptor) {
  if (!descriptor || typeof descriptor !== "object") return null;
  const keys = Object.keys(descriptor);
  return keys.length ? keys[0] : null; // e.g. "ExPt", "WhMo", ...
}

function reducer(state, action) {
  switch (action.type) {
    case "INIT":
      return action.payload || {};
    case "SELECT_OPTION": {
      const opt = action.payload; // { groupKey, name, price, image, descriptor, path? }
      return { ...state, [opt.groupKey]: opt };
    }
    case "REMOVE_OPTION": {
      const { [action.payload]: _, ...rest } = state;
      return rest;
    }
    case "CLEAR_ALL":
      return {};
    default:
      return state;
  }
}

const getInitialState = () => {
  try {
    const persistedState = localStorage.getItem(STORAGE_KEY);
    // If state exists in local storage, parse it.
    if (persistedState) {
      return JSON.parse(persistedState);
    }
  } catch (e) {
    console.error("Could not parse state from localStorage", e);
  }
  // Otherwise, return the default empty state.
  return {};
};

// Function to safely read initial config state from localStorage
const getInitialConfig = () => {
  try {
    const persistedConfig = localStorage.getItem(STORAGE_KEY_CONFIG);
    if (persistedConfig) {
      return JSON.parse(persistedConfig);
    }
  } catch (e) {
    console.error("Could not parse config from localStorage", e);
  }
  // Fallback to safe defaults if nothing is stored or parsing fails
  return {
    ar: "16:9",
    res: "1920 x 1080",
    overlay: false,
  };
};

export function SelectedOptionsProvider({ children }) {
  const initialConfig = getInitialConfig();

  const saveMenuOption = localStorage.getItem("menuOption") || "options";

  const [state, dispatch] = useReducer(reducer, getInitialState());
  const [currentMenuOption, setCurrentMenuOption] = useState(saveMenuOption);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [currentAspectRatio, setCurrentAspectRatio] = useState(
    initialConfig.ar
  );
  const [currentResolution, setCurrentResolution] = useState(initialConfig.res);
  const [showDynamicViewportOverlay, setShowDynamicViewportOverlay] = useState(
    initialConfig.overlay
  );

  const selectMenuOption = useCallback(
    (option) => {
      setCurrentMenuOption(option);
      localStorage.setItem("menuOption", option);
    },
    [] // dispatch from useReducer is a stable reference
  );

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) dispatch({ type: "INIT", payload: JSON.parse(raw) });
    } catch {}
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.error("Could not save state to localStorage", e);
    }
  }, [state]);

  const items = useMemo(() => Object.values(state), [state]);
  const subtotal = useMemo(
    () => items.reduce((sum, item) => sum + Number(item.price || 0), 0),
    [items]
  );

  const selectOption = useCallback(
    (raw) => {
      const groupKey =
        getGroupKeyFromDescriptor(raw?.descriptor) || raw.groupKey || "misc";
      const price = Number(raw?.price);
      const payload = { ...raw, price, groupKey };
      dispatch({ type: "SELECT_OPTION", payload });
    },
    [dispatch] // dispatch from useReducer is a stable reference
  );

  const removeOption = useCallback(
    (groupKey) => dispatch({ type: "REMOVE_OPTION", payload: groupKey }),
    [dispatch]
  );

  const clearAll = useCallback(
    () => dispatch({ type: "CLEAR_ALL" }),
    [dispatch]
  );

  useEffect(() => {
    const configString = createUrlConfigString(state);

    // --- Determine current URL state ---
    const pathSegments = window.location.pathname.split("/").filter(Boolean);
    const currentConfigCode =
      pathSegments.length > 0 ? pathSegments[pathSegments.length - 1] : "";

    // Prevent update if the new string matches the current path parameter
    if (configString === currentConfigCode) return;

    // --- Construct New Path ---
    let newPathSegments = [...pathSegments];

    // If the last segment is the configCode, remove it before adding the new one
    // eslint-disable-next-line no-self-compare
    if (currentConfigCode === currentConfigCode) {
      newPathSegments.pop();
    }

    // Add the new configuration string if it's not empty
    if (configString) {
      newPathSegments.push(configString);
    }

    // Construct the final URL (e.g., /config/ExPt-prm_FrLi-wcf)
    const newPath = "/" + newPathSegments.join("/") || "/";

    // Preserve any query parameters (like ?ar=16:9)
    const newUrl = newPath + window.location.search;

    // Use replaceState to change the URL without adding a new entry to browser history
    window.history.replaceState({ path: newUrl }, "", newUrl);
  }, [state]); // Run whenever the selected options map changes

  // NEW: Stable setter for resolution
  const updateResolution = useCallback(
    (value) => setCurrentResolution(value),
    []
  );

  // FIX: Ensure existing aspectRatio update is also wrapped
  const updateAspectRatio = useCallback(
    (value) => setCurrentAspectRatio(value),
    []
  );

  // FIX: Ensure existing overlay toggle is also wrapped
  const toggleShowDynamicViewportOverlay = useCallback((value) => {
    // Allows toggling or setting a specific value
    if (typeof value === "boolean") {
      setShowDynamicViewportOverlay(value);
    } else {
      setShowDynamicViewportOverlay((v) => !v);
    }
  }, []);

  // ---- summary popup controls (available app-wide)
  const openSummary = () => setSummaryOpen(true);
  const closeSummary = () => setSummaryOpen(false);
  const toggleSummary = () => setSummaryOpen((v) => !v);

  // NEW EFFECT: Persist configuration state separately
  useEffect(() => {
    try {
      const configToSave = {
        ar: currentAspectRatio,
        res: currentResolution,
        overlay: showDynamicViewportOverlay,
      };
      localStorage.setItem(STORAGE_KEY_CONFIG, JSON.stringify(configToSave));
    } catch (e) {
      console.error("Could not save config to localStorage", e);
    }
  }, [currentAspectRatio, currentResolution, showDynamicViewportOverlay]);
  // setCurrentAspectRatio((value) => );

  return (
    <SelectedOptionsContext.Provider
      value={{
        items,
        map: state,
        subtotal,
        selectOption,
        removeOption,
        clearAll,
        summaryOpen,
        openSummary,
        closeSummary,
        toggleSummary,
        currentAspectRatio,
        updateAspectRatio,
        currentResolution,
        updateResolution,
        showDynamicViewportOverlay,
        toggleShowDynamicViewportOverlay,
        currentMenuOption,
        selectMenuOption,
      }}
    >
      {children}
    </SelectedOptionsContext.Provider>
  );
}

export function useSelectedOptions() {
  const ctx = useContext(SelectedOptionsContext);
  if (!ctx)
    throw new Error(
      "useSelectedOptions must be used within SelectedOptionsProvider"
    );
  return ctx;
}
